import { Plus, Users, Trash2, CalendarDays, BarChart3, Settings as SettingsIcon, LogOut, MoreVertical } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useAuth } from "@/lib/auth";

export function Sidebar({
  pipelines, selectedPipeline, onSelectPipeline, onNewPipeline, onDeletePipeline,
  onManageTeam, onOpenSettings, contactCounts, currentView, onChangeView,
}) {
  const { user, logout } = useAuth();
  return (
    <aside data-testid="sidebar" className="w-72 h-screen flex flex-col bg-[#18181B] border-r border-zinc-800 flex-shrink-0">
      <div className="px-6 pt-6 pb-3">
        <div className="flex items-center gap-2.5">
          <img src="/connect-icon.svg" alt="" className="h-7 w-7 flex-shrink-0" />
          <div>
            <p className="text-[10px] font-semibold tracking-widest leading-none salonup-logo-badge">SalonUp</p>
            <h1 className="text-xl font-bold tracking-tight text-zinc-50 leading-tight">CONNECT</h1>
          </div>
        </div>
      </div>

      {user && (
        <div className="px-6 pb-3 border-b border-zinc-800/70" data-testid="sidebar-salao">
          <p className="text-[10px] uppercase tracking-wider text-zinc-500">Salão</p>
          <p className="text-sm font-medium text-zinc-200 truncate" title={user.nome}>{user.nome}</p>
          <p className="text-[10px] text-zinc-500 truncate">{user.email}</p>
        </div>
      )}

      <div className="px-4 pt-4 pb-4">
        <button
          data-testid="new-pipeline-btn"
          onClick={onNewPipeline}
          className="w-full flex items-center gap-2 px-3 py-2.5 rounded-md bg-amber-500 text-zinc-950 hover:bg-amber-600 transition-all duration-200 text-sm font-semibold"
        >
          <Plus className="h-4 w-4" />
          Nova campanha
        </button>
      </div>

      <div className="px-6 pb-2">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">Campanhas</span>
      </div>

      <div className="flex-1 min-h-0 px-2 overflow-y-auto overflow-x-hidden" data-testid="sidebar-pipelines-list">
        <div className="space-y-0.5">
          {pipelines.map((p) => {
            const isActive = p.id === selectedPipeline && currentView === "kanban";
            const count = contactCounts[p.id] || 0;
            return (
              <div
                key={p.id}
                data-testid={`sidebar-pipeline-${p.id}`}
                onClick={() => { onSelectPipeline(p.id); onChangeView("kanban"); }}
                className={`group flex items-center justify-between gap-1 pl-4 pr-1.5 py-2.5 rounded-md w-full min-w-0 cursor-pointer transition-all duration-200 ${isActive ? "salonup-sidebar-active" : "text-zinc-400 hover:bg-zinc-800/50 hover:text-zinc-200"}`}
              >
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  {isActive && <div className="w-0.5 h-5 bg-amber-500 rounded-full flex-shrink-0" />}
                  <span className="text-sm truncate font-medium" title={p.nome}>{p.nome}</span>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {count > 0 && <span className="text-[10px] text-zinc-500 bg-zinc-800 px-1.5 py-0.5 rounded">{count}</span>}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        data-testid={`pipeline-menu-${p.id}`}
                        onClick={(e) => e.stopPropagation()}
                        className="flex-shrink-0 text-zinc-400 hover:text-zinc-100 hover:bg-zinc-700/60 rounded p-1 transition-colors duration-200"
                        aria-label="Opções da campanha"
                      >
                        <MoreVertical className="h-4 w-4" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                      <DropdownMenuItem
                        data-testid={`delete-pipeline-${p.id}`}
                        disabled={pipelines.length <= 1}
                        onSelect={() => onDeletePipeline(p)}
                        className="text-red-600 focus:text-red-600 text-xs"
                      >
                        <Trash2 className="h-3.5 w-3.5 mr-2" />
                        {pipelines.length <= 1 ? "Última campanha (não pode excluir)" : "Excluir campanha"}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            );
          })}
          {pipelines.length === 0 && (
            <p className="px-4 py-3 text-xs text-zinc-500">Crie sua primeira campanha.</p>
          )}
        </div>
      </div>

      <div className="p-4 border-t border-zinc-800 space-y-0.5">
        <button
          data-testid="nav-agenda-btn"
          onClick={() => onChangeView("agenda")}
          className={`w-full flex items-center gap-2 px-3 py-2 rounded-md transition-all duration-200 text-sm ${currentView === "agenda" ? "salonup-sidebar-active" : "text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200"}`}
        >
          <CalendarDays className="h-4 w-4" />
          <span>Agenda comercial</span>
        </button>
        <button
          data-testid="nav-relatorios-btn"
          onClick={() => onChangeView("relatorios")}
          className={`w-full flex items-center gap-2 px-3 py-2 rounded-md transition-all duration-200 text-sm ${currentView === "relatorios" ? "salonup-sidebar-active" : "text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200"}`}
        >
          <BarChart3 className="h-4 w-4" />
          <span>Relatórios</span>
        </button>
        <button data-testid="manage-team-btn" onClick={onManageTeam} className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200 transition-all duration-200 text-sm">
          <Users className="h-4 w-4" />
          <span>Equipe</span>
        </button>
        <button data-testid="open-settings-btn" onClick={onOpenSettings} className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200 transition-all duration-200 text-sm">
          <SettingsIcon className="h-4 w-4" />
          <span>Configurações</span>
        </button>
        <button data-testid="logout-btn" onClick={logout} className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-zinc-400 hover:bg-zinc-800 hover:text-red-300 transition-all duration-200 text-sm">
          <LogOut className="h-4 w-4" />
          <span>Sair</span>
        </button>
      </div>
    </aside>
  );
}
